import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import {
  useGetProfileQuery,
  useLazyGetPostGroupQuery,
  useLazyGetOrganizationDetailsQuery,
  useLazyGetOrganizationProfileQuery,
} from "../../redux/reducers/apiSlice";
import { MESSAGE } from "../Constants/Message";
import * as Sentry from "@sentry/react";
import { useSelector } from "react-redux";
import { tokenSelector } from "../../redux/reducers/authSlice";

export const useOrganization = () => {
  const { organizationId, sparkName } = useParams();
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

  const { data: currentUserProfile, isLoading: currentUserProfileLoading } =
    useGetProfileQuery();

  const [getOrganizationDetails, { isLoading, data: organizationData }] =
    useLazyGetOrganizationDetailsQuery();

  const [
    getOrganizationProfile,
    { isLoading: isOrganizationProfileLoading, data: organizationProfileData },
  ] = useLazyGetOrganizationProfileQuery();

  const isAuthenticated = useSelector(tokenSelector);

  const [
    getOrganizationPostGroup,
    { data: posts, isLoading: organizationPostsLoading },
  ] = useLazyGetPostGroupQuery();

  const myOrganizations = currentUserProfile?.organizations || [];

  // Fetch basic organization details whenever the organizationId changes.
  // This should not refire just because auth state flips, to avoid blinking / extra loading.
  useEffect(() => {
    if (organizationId) {
      setHasAttemptedLoad(false); // Reset when orgId changes
      getOrganizationDetailsFn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  // Fetch organization posts only when the user is authenticated and an organizationId exists.
  // Separated into its own effect so it doesn't cause repeated refetches of org details.
  useEffect(() => {
    if (organizationId && isAuthenticated) {
      getOrganizationPostGroup(organizationId);
    }
  }, [organizationId, isAuthenticated, getOrganizationPostGroup]);

  const getOrganizationDetailsFn = async () => {
    if (!organizationId) return;

    setHasAttemptedLoad(true); // Mark that we've started loading

    try {
      // Always try authenticated endpoint first if authenticated
      if (isAuthenticated) {
        try {
          await getOrganizationDetails(organizationId).unwrap();
          return; // Success, exit early
        } catch (error: any) {
          // If 404, try public profile endpoint as fallback
          if (error?.status === 404 || error?.data?.status === 404) {
            try {
              await getOrganizationProfile(organizationId).unwrap();
              return; // Success with public profile
            } catch (profileError: any) {
              // Both failed, log but don't show error toast yet
              console.warn('Both authenticated and public profile endpoints failed:', profileError);
              // Don't throw - let the component handle the "no org" state
              return;
            }
          } else {
            // Other error (500, network, etc) - log but don't show toast immediately
            console.error('Error fetching organization details:', error);
            // Don't throw - let the component handle the "no org" state
            return;
          }
        }
      } else {
        // Unauthenticated users always see the public profile
        try {
          await getOrganizationProfile(organizationId).unwrap();
        } catch (error: any) {
          console.warn('Public profile endpoint failed:', error);
          // Don't throw - let the component handle the "no org" state
        }
      }
    } catch (e) {
      // Only log unexpected errors, don't show toast (component will show "No organization found")
      console.error('Unexpected error in getOrganizationDetailsFn:', e);
      Sentry.captureException(e);
    }
  };

  const orgMembers = useMemo(() => {
    return organizationData?.members || [];
  }, [organizationData?.id]);

  const admins = useMemo(() => {
    return organizationData?.members?.filter(
      (member) => member.role === "ADMIN"
    );
  }, [organizationData?.id]);

  const getRole = (memberId: string) => {
    return orgMembers.find((member) => member.id === memberId)?.role;
  };

  const goalsBySparkName = useMemo(() => {
    return (
      organizationData?.goals?.filter(
        (goal) => goal.spark.name === sparkName
      ) || []
    );
  }, [sparkName, organizationData?.goals]);

  const isCurrentUserAdmin = useMemo(() => {
    if (admins && admins.length > 0) {
      return !!admins.find((admin) => admin?.id === currentUserProfile?.id);
    }

    return false;
  }, [admins, currentUserProfile?.id]);

  const isCurrentUserMember = useMemo(() => {
    if (orgMembers && orgMembers.length > 0) {
      return !!orgMembers.find(
        (member) => member?.id === currentUserProfile?.id
      );
    }

    return false;
  }, [orgMembers, currentUserProfile?.id]);

  return {
    currentOrganization: organizationData || organizationProfileData,
    currentOrganizationLoading: isLoading || isOrganizationProfileLoading || !hasAttemptedLoad,
    members: orgMembers,
    refetch: getOrganizationDetailsFn,
    getRole,
    goalsBySparkName,
    organizationPosts: posts,
    organizationPostsLoading,
    myOrganizations,
    myOrganizationsLoading: currentUserProfileLoading,
    admins,
    isCurrentUserAdmin,
    isCurrentUserMember,
  };
};
